import React from "react";
import { View, StyleSheet, ScrollView } from "react-native";
import { PreviewSection, LookupField } from "../shared";
import { TextInput } from "@/components/ui/TextInput";
import { useEntityEdit } from "@/contexts/EntityEditContext";
import useAuth from "@/hooks/useAuth";
import { ClientInput } from "@/components/inputs/ClientInput";
import { ProjectInput } from "@/components/inputs/ProjectInput";

export interface BillingItemData {
  id: string;
  client_id?: string | null;
  project_id?: string | null;
  meta_data?: {
    description?: string | null;
    rate?: string | null;
    quantity?: number | null;
    unit?: string | null;
    amount?: number | null;
  } | null;
  client?: {
    id?: string;
    name?: string;
  } | null;
  project?: {
    id?: string;
    name?: string;
  } | null;
}

export interface BillingItemOverviewTabProps {
  billingItem: BillingItemData | null;
  loading?: boolean;
}

export function BillingItemOverviewTab({ billingItem, loading = false }: BillingItemOverviewTabProps) {
  const { editing, formData, updateFormData } = useEntityEdit<BillingItemData>();
  const { displayNames } = useAuth();

  // Unified data source
  const displayData = editing ? formData : billingItem;

  // ---------- Handlers ----------

  const handleClientChange = (clientId: string | null) => {
    updateFormData({
      ...formData,
      client_id: clientId,
      project_id: null, // Clear project when client changes
      project: null,
    } as any);
  };

  const handleProjectChange = (projectId: string | null) => {
    updateFormData({
      ...formData,
      project_id: projectId,
    } as any);
  };

  const handleDescriptionChange = (text: string) => {
    const currentMeta = (formData?.meta_data as Record<string, any>) || {};
    updateFormData({
      ...formData,
      meta_data: {
        ...currentMeta,
        description: text || null,
      },
    } as any);
  };

  const handleRateChange = (text: string) => {
    const currentMeta = (formData?.meta_data as Record<string, any>) || {};
    updateFormData({
      ...formData,
      meta_data: {
        ...currentMeta,
        rate: text || null,
      },
    } as any);
  };

  const handleQuantityChange = (text: string) => {
    const currentMeta = (formData?.meta_data as Record<string, any>) || {};
    const qtyNum = parseFloat(text) || null;
    updateFormData({
      ...formData,
      meta_data: {
        ...currentMeta,
        quantity: qtyNum,
      },
    } as any);
  };

  const handleUnitChange = (text: string) => {
    const currentMeta = (formData?.meta_data as Record<string, any>) || {};
    updateFormData({
      ...formData,
      meta_data: {
        ...currentMeta,
        unit: text || null,
      },
    } as any);
  };

  return (
    <View style={styles.container} pointerEvents={loading ? "none" : "auto"}>
      <ScrollView contentContainerStyle={{ gap: 8, padding: 16 }}>
        {/* Project (required) */}
        {editing ? (
          <>
            <ClientInput
              value={displayData?.client_id || null}
              state={loading ? "loading" : "editing"}
              label={displayNames?.clients?.name || "Client"}
              placeholder="Filter by client (optional)"
              onChange={handleClientChange}
            />
            <ProjectInput
              value={displayData?.project_id || null}
              clientId={displayData?.client_id || null}
              state={loading ? "loading" : "editing"}
              label={`${displayNames?.projects?.name || "Project"} *`}
              placeholder="Select a project"
              onChange={handleProjectChange}
            />
          </>
        ) : (
          <>
            <LookupField
              label={displayNames?.clients?.name || "Client"}
              value={displayData?.client?.name}
              placeholder={`No ${displayNames?.clients?.name?.toLowerCase() || "client"}`}
              icon="office-building"
              loading={loading}
              params={displayData?.client?.id ? { panel: "client", id: displayData.client.id } : undefined}
            />
            <LookupField
              label={displayNames?.projects?.name || "Project"}
              value={displayData?.project?.name}
              placeholder={`No ${displayNames?.projects?.name?.toLowerCase() || "project"}`}
              icon="folder"
              loading={loading}
              params={displayData?.project?.id ? { panel: "project", id: displayData.project.id } : undefined}
            />
          </>
        )}

        {/* Description */}
        <PreviewSection title="Description" titleStyle={styles.label}>
          <TextInput
            value={(displayData?.meta_data as any)?.description || ""}
            onChangeText={handleDescriptionChange}
            multiline
            numberOfLines={3}
            editable={editing}
            loading={loading}
            placeholder="Enter description..."
          />
        </PreviewSection>

        {/* Rate & Quantity */}
        <PreviewSection title="Billing Details" titleStyle={styles.label}>
          <View style={styles.row}>
            <View style={{ flex: 1 }}>
              <TextInput
                label="Rate"
                value={(displayData?.meta_data as any)?.rate || ""}
                onChangeText={handleRateChange}
                editable={editing}
                loading={loading}
                placeholder="e.g. $35.00/hr"
              />
            </View>
            <View style={{ flex: 1 }}>
              <TextInput
                label="Quantity"
                value={String((displayData?.meta_data as any)?.quantity || "")}
                onChangeText={handleQuantityChange}
                editable={editing}
                loading={loading}
                placeholder="e.g. 8"
              />
            </View>
            <View style={{ flex: 1 }}>
              <TextInput
                label="Unit"
                value={(displayData?.meta_data as any)?.unit || ""}
                onChangeText={handleUnitChange}
                editable={editing}
                loading={loading}
                placeholder="hr, day, etc."
              />
            </View>
          </View>
          <LookupField
            label="Amount"
            value={
              (displayData?.meta_data as any)?.amount != null
                ? `$${Number((displayData?.meta_data as any).amount).toFixed(2)}`
                : undefined
            }
            placeholder="—"
            readonly
            loading={loading}
          />
        </PreviewSection>
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  label: {
    fontSize: 11,
    fontWeight: "600",
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  row: {
    flexDirection: "row",
    gap: 8,
  },
});
